import api, { ApiResponse } from './api';
import {
  approvalSlotActorName,
  isApprovalSignatureParked,
  PARKED_SLOT_DISPLAY_NAME,
} from './approvalSignatureParked';
import { REPORT_STATUS_LABELS, latestRoundSignatures } from '@safetyhub/shared';
import { signatureRound } from './approvalRound';
import type { ReportContactSignature, BaseSignatureMethod, ApprovalSignatureMode, ApprovalLineDefinitionRow, ReportTemplate, DelegationProgress, DelegationProgressState, TbmSignatureMode, ExternalSignatureReportView, ApprovalLinePreviewData, ReportApprovalLine, SlotSignatureWireMap, SlotSignatureWireView } from '@safetyhub/shared';
import type { BadgeTone } from '@safetyhub/ui';

export type { ReportContactSignature } from '@safetyhub/shared';

// Types
//
// 저장용 유형 union도 `Report.type` 필드도 없다 — `reports` 행이 dev·prod 모두 `COMPOSED`뿐이라
// 값 하나뿐인 축은 아무것도 구분하지 못한다. 문서의 종류 축은 `templateKey`/`templateName`(양식)이고,
// 표시 라벨의 입력은 양식 이름뿐이다(아래 Helpers 절).
// 유형을 코드에 다시 열거하거나 `type === '<값>'` 분기를 새로 만들지 말 것.
/**
 * 라벨 정본(`REPORT_STATUS_LABELS`, Prisma `enum ReportStatus` 6종)에서 **파생**한다.
 *
 * ⚠️ 손나열하면 반드시 낡는다 — 실제로 `CANCELLED`가 빠져 있었고, 소비처가 대개
 * `labels[status] || status` 폴백이라 그 상태의 문서는 조용히 raw enum이 찍혔다.
 * shared의 `SplitReportStatus`가 같은 이유로 같은 방식을 쓴다.
 */
export type ReportStatus = keyof typeof REPORT_STATUS_LABELS;
export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
export type SignatureMethod = BaseSignatureMethod;

// PDF Status Type
export type PdfStatus = 'NONE' | 'PENDING' | 'GENERATING' | 'COMPLETED' | 'FAILED';

export interface Report {
  id: string;
  companyId: string;
  siteId: string;
  authorId: string;
  /**
   * Composer 템플릿 키 (양식 식별자 — 문서 종류 축).
   * non-null — DB `reports.template_key`가 NOT NULL이고 모든 문서가 Composer 문서라 서버 응답에 항상 실린다.
   */
  templateKey: string;
  /**
   * 분할 생성(1점검 → N문서)으로 태어난 문서의 그룹 키. NULL = 분할이 아님.
   * 그룹 단위 중복 가드의 축이라 **사람이 읽는 값이 아닐 수 있다**(커스텀 접두·도메인 코드).
   */
  splitGroupKey?: string | null;
  /**
   * 그 그룹의 표시명 — 형제 칩(`SiblingReportChips`)이 그리는 라벨.
   * NULL이면 소비처가 제목으로 폴백한다. 결정: 2026-09-01 「Composer 분할 생성」 §④.
   */
  splitGroupLabel?: string | null;
  /**
   * 묶음 생성 배치(위임 라운드) — 형제 파생(`useSiblingReports`)이 같은 점검·양식의
   * **다른 라운드** 문서를 거르는 축(F-5). NULL = 도입 전 기존 행(null끼리 종전 판정 유지).
   */
  splitBatchId?: string | null;
  /**
   * Composer 양식 이름 (서버가 `templateKey`로 조인해 채운다).
   * 유형 배지가 보여줄 사람이 읽는 이름 — `getReportTypeLabel` 인자.
   */
  templateName?: string | null;
  /**
   * 생성 시점에 박제된 템플릿 스냅샷 (무결성). 존재하면 상세 렌더는 live fetch 대신
   * 이 스냅샷을 사용한다 — 확정 후 템플릿이 수정돼도 과거 보고서는 불변(법적 증빙).
   */
  templateSnapshot?: ReportTemplate | null;
  title: string;
  content: ReportContent;
  status: ReportStatus;
  fileUrl?: string; // PDF 파일 URL
  pdfStatus?: PdfStatus; // PDF 생성 상태
  pdfError?: string; // PDF 생성 실패 시 에러 메시지
  /** PENDING 삭제 요청 존재 여부 */
  hasPendingDeleteRequest?: boolean;
  /**
   * **최신 라운드에 제3자 결재 승인 서명이 있는가** — findAll 서버 파생의 «문서 사실»이며
   * `status`와 무관하다(공용 제3자 판정: author 단계·미지정 칸·작성자 본인 서명 제외).
   *
   * ⚠️ 아래 `requiresDeleteApproval`과 **층이 다르다**. 저건 이 사실 + actor + status의
   * «결론»이고 `APPROVED`에서만 참인데, `PENDING_APPROVAL`의 작성자도 승인 요청 경로다
   * (Owner는 같은 상태에서 즉시 삭제). actor를 아는 화면만 그 조합을 할 수 있으므로 서버는
   * 사실만 준다 — 조합은 `pages/reports/deletePermission`이 한 벌로 소유한다.
   */
  hasThirdPartyApproval?: boolean;
  /**
   * 삭제에 제3자 승인 요청이 필요한가 — findAll 서버 파생(공용 제3자 판정: author 단계·
   * 미지정 칸·작성자 본인 서명 제외, 최신 라운드). 목록의 프론트 재파생 금지(2026-08-17).
   *
   * ⚠️ **`APPROVED` 한정 결론이다.** 목록 삭제 흐름은 이제 위 `hasThirdPartyApproval`을
   * actor와 조합해 파생하므로 이 필드를 읽지 않는다(`PENDING_APPROVAL` 축을 못 담아서).
   */
  requiresDeleteApproval?: boolean;
  /** 삭제 승인자(제3자 최고 level) 이름 — requiresDeleteApproval=true일 때만 의미. */
  deleteApproverName?: string | null;
  /**
   * 결재완료(APPROVED) 보고서의 최종 결재자가 받을 수 있는 알림 채널.
   * 삭제 요청/재전송 모달의 채널 선택(가용 2개=선택, 1개=자동)에 사용.
   * 결재완료가 아니면 서버가 null로 응답.
   */
  deletableApproverChannels?: { sms: boolean; email: boolean } | null;
  // 작성자 서명 정보
  authorSignedAt?: string;
  authorSignerName?: string;
  authorSignerTitle?: string;
  authorSignatureData?: string;
  /**
   * 완료자 서명 한 벌(F-45, 결정 2026-09-13) — 위임 문서를 결재 없이 「완료」할 때 완료자(위임한
   * 사람)가 남긴 서명. 작성자 서명(`author*`)은 담당자의 것이라 그대로 보존된다. 저장만 되고
   * **표시 자리는 아직 없다**. 대상이 아닌 문서는 전부 null.
   */
  completerSignedAt?: string | null;
  completerSignerName?: string | null;
  completerSignerTitle?: string | null;
  completerSignatureData?: string | null;
  completerSignatureMethod?: SignatureMethod | null;
  completerUserId?: string | null;
  /** 보고서 작성 시점 스냅샷 이름 (서버 신설 필드, 탈퇴/purge 후에도 원본 유지) */
  authorSnapshotName?: string | null;
  // (`useInspectionSignatures`(점검완료 서명 사용 여부)는 라운드 5(W-l)에서 이 타입에서 뺀 뒤 2026-09-20 서버
  //  컬럼 자체가 drop — 응답에도 더는 실리지 않는다.)
  // 반려 후 수정 여부
  editedAfterRejection?: boolean;
  // 현장+유형별 순번 (N차)
  sequenceNumber?: number;
  // 결재완료일 (APPROVED 상태의 최종 서명자 signedAt)
  approvedAt?: string | null;
  // 결재 서명 모드 (ELECTRONIC | OFFLINE)
  approvalSignatureMode?: ApprovalSignatureMode | null;
  /**
   * TBM 교육 서명 방식 — **"배포 설정 완료" 파생의 정본 축**(결정 2026-09-06 §5:
   * 배포 설정의 모든 갈래가 서명 방식 확정을 지나고, 확정 후엔 불변). NULL = 미설정
   * (composed 생성 경로는 미설정으로 태어난다) 또는 legacy/비TBM. 판정 자체는
   * `pages/reports/postSubmitFlows.ts` 레지스트리가 소유한다 — 화면에서 직접 비교하지 말 것.
   */
  tbmSignatureMode?: TbmSignatureMode | null;
  /**
   * TBM 교육 세션 수(상세 응답 전용 파생 스칼라) — 「TBM 담당자 확인」 서명은 세션 educator
   * 서명뿐이라(결정 2026-09-08) 0건이면 그 칸이 빈칸으로 남는다. 그 사유 표시와 완료 전
   * 경고가 이 축을 읽는다. 판정은 `lib/tbmConductorSignature`가 소유한다 — 화면에서 직접
   * 0과 비교하지 말 것.
   *
   * 상세는 세션 행 길이에서, 목록은 `_count`에서 파생한다(2026-09-08 — 종전엔 목록에 없었다).
   * 그래도 **옛 응답엔 없으므로**(undefined) 그때는 **"모른다"**로 읽는다(0으로 읽지 않는다) —
   * 삭제 경고처럼 되돌릴 수 없는 판정은 모를 때 강한 쪽으로 간다.
   */
  educationSessionCount?: number;
  /**
   * 이 문서는 **작성자가 직접 교육을 진행한다**(결정 2026-09-08 「안전관리자가 직접 진행하는
   * TBM 교육」). 담당자 지정의 **배타 갈래**라 참이면 교육 담당자도 발송 토큰도 없다.
   *
   * 화면이 읽는 이유는 하나 — 「담당자 확인 서명란이 왜 비었나」의 문구가 갈린다(기다릴
   * **담당자가 없는** 문서에 "담당자가 아직…"이라 적으면 지정을 빠뜨린 것으로 읽힌다).
   * 판정은 `lib/tbmConductorSignature`가 소유한다 — 화면에서 직접 비교하지 말 것.
   *
   * ⚠️ **상세 응답에만 실린다**(`findOne`은 include라 스칼라 전량 / 목록 `findAll`은 select
   * 화이트리스트에 없다). 목록 형상에서는 `undefined`다 — 새 소비처를 붙이기 전에 그 응답이
   * 이 축을 싣는지 확인할 것.
   */
  tbmSelfConducted?: boolean;
  /**
   * 교육 담당자 **발송 스냅샷**(F-27, 2026-09-11) — 배포 설정 확정(또는 진행자 지정)이 만든 최신
   * 활성 direct 토큰의 연락처. 「교육 링크 재전송」 버튼의 노출 게이트와 모달의 이름·채널 가용성이
   * 읽는다. 담당자 축이 `tbmEducatorContactId`가 아니라 이것인 이유: 목록에서 고르지 않고
   * 이름·연락처만 적은 담당자는 그 컬럼이 null이고, 실제 발송 대상은 어차피 토큰의 스냅샷이다.
   *
   * `null` = 보낼 링크가 없다(직접 진행·OFFLINE·미확정·취소본). **상세 응답에만 실린다**
   * (`findOne` 전용 include) — 목록·옛 응답에서는 `undefined`이고, 그때는 없는 것으로 읽는다(버튼이
   * 안 뜰 뿐 되돌릴 수 없는 판정이 아니다). 판정은 `pages/reports/postSubmitFlows`의 `resendEntry`가
   * 소유한다 — 화면에서 직접 비교하지 말 것.
   */
  tbmEducatorDispatch?: {
    contactName: string;
    contactPhone: string;
    contactEmail?: string | null;
    /**
     * 담당자가 링크를 처음 연 시각(F-53, 2026-09-13). null = 아직 안 열었다. 옛 응답은 `undefined`
     * (모른다)라 그때도 「발송됨」으로 읽는다 — 문구는 `postSubmitFlows`의 `conductorNotice`가 소유.
     */
    firstAccessedAt?: string | null;
  } | null;
  /**
   * 이어질 흐름의 **선언 키** — 목록 응답이 `templateSnapshot`을 실을 수 없어(평균 93KB)
   * 서버가 그 스냅샷에서 파생해 주는 스칼라다(2026-09-08 dev 실측에서 적발: 상세는 「진행 중」인데
   * 목록만 「초안」이었다). 상세 응답엔 스냅샷이 실리므로 이 값이 없어도 된다.
   *
   * 직접 비교하지 말 것 — 판정은 `pages/reports/postSubmitFlows`의 `reportPostSubmitFlow`가
   * 소유하고, 그 함수가 스냅샷을 먼저 보고 없을 때만 이 키로 폴백한다.
   */
  postSubmitFlowKey?: string | null;
  /**
   * TBM 교육 세션 **행**(상세 응답 전용, `sessionDate` 오름차순) — 「교육 중 · 2/3일」 배지와
   * 회차 줄의 재료다(결정 2026-09-08 「TBM 교육 진행 상태 축」). 위 스칼라는 이 행 길이에서
   * 파생되므로 "세션이 몇 개인가"의 답이 두 곳에서 갈리지 않는다.
   *
   * 진행 판정은 `lib/tbmEducationProgress` → shared `deriveTbmEducationProgress`가 소유한다 —
   * 화면에서 길이를 세거나 상태를 직접 비교하지 말 것(분자 축이 세션이 아니라 **서로 다른
   * 날**이라, 손으로 세면 같은 날 두 세션을 이틀로 읽는다).
   *
   * 목록 응답과 옛 응답엔 없다(undefined) — 위 스칼라와 같은 **"모른다"** 계약이다.
   */
  educationSessions?: Array<{
    sessionDate: string;
    status: string;
    /**
     * 이 회차에 「교육 시작」(QR 서명 세션 생성)이 있었나(F-55, 2026-09-13) — 첫 QR 생성 시각.
     * **`null` = 아직 시작 전**(담당자가 입력만 해서 lazy 생성된 회차 → 배지 「교육 준비 중」),
     * **`undefined` = 모른다**(옛 응답·목록 형상 → 종전대로 「교육 중」). 판정은
     * `lib/tbmEducationProgress` → shared `deriveTbmEducationProgress`가 소유한다 — 화면에서
     * 직접 null과 비교하지 말 것. 값은 `EducationSession.startedAt` 컬럼 그대로 — 두 「교육 시작」
     * 경로(`createQRSession`·`startEducationByToken`)가 찍고 시작 전 자동저장의 lazy 생성은 null로 둔다.
     */
    startedAt?: string | null;
    /**
     * 이 회차에 **사람이 남긴 것이 하나도 없는가** — 참석자 서명·현장 기록·담당자 서명·
     * 안전점검 메모·안전조치 확인·특이사항이 전부 빈가(결정 2026-09-09).
     *
     * 서버가 shared `isEmptyTbmEducationSession`으로 파생해 **불리언 하나로 접어** 내려준다.
     * 확정 시 빈 진행 중 회차를 지우는 서버 로직이 **같은 술어**를 보므로, 화면이 「제외됩니다」라
     * 안내한 회차와 실제로 지워지는 회차가 갈리지 않는다.
     *
     * **필드 자체가 optional인 이유**는 위 배열과 같다 — 이 필드가 없던 시절의 응답·픽스처에서는
     * `undefined`이고, 그것은 「비었다」가 **아니라 「모른다」**다. 모르는 것을 「빔」으로 읽으면
     * 지워지지 않을 회차를 지워진다고 안내하게 된다(`lib/tbmConductorSignature` 참조).
     */
    isEmpty?: boolean;
  }>;
  // TBM 전용 필드
  completedAt?: string;
  educationStartDate?: string | null;
  educationEndDate?: string | null;
  attendeeCount?: number;
  distributionTokens?: Array<{ contactName: string }>;
  qrSessions?: Array<{ createdAt: string }>;
  /** 보고서 생성 시 pre-select된 현장 담당자 목록 (Phase 2-H) */
  preSelectedContacts?: Array<{
    id: string;
    name: string;
    phone?: string | null;
    position?: string | null;
    department?: string | null;
  }>;
  /** 보고서 현장 담당자 서명 목록 (Phase 2-H) */
  contactSignatures?: ReportContactSignature[];
  /** DLR 위임받은 현장 담당자 ID */
  delegatedContactId?: string | null;
  /** DLR 위임받은 현장 담당자 */
  delegatedContact?: {
    id: string;
    name: string;
    phone?: string | null;
    email?: string | null;
    /**
     * 담당자가 **아직 위임 대상으로 쓸 수 있는가**(⑦-b, 2026-08-30) — 서버가 담당자 행
     * `isActive`·멤버·계정 소실을 접어 내린 파생값이다. `false`면 서버의 단순 재전송이 400으로
     * 거부되므로 화면도 재전송을 비활성으로 정렬한다(교체·해제는 그 출구라 그대로 연다).
     * 옛 응답엔 없다(undefined) — 그때는 "살아 있음"으로 읽는다.
     */
    isActive?: boolean;
  };
  /** DLR 위임 활성 토큰 존재 여부 */
  hasActiveDelegationToken?: boolean;
  /** DLR 위임 활성 토큰 채널 상태 (재전송 모달 default 복원용) */
  activeDelegationToken?: { smsStatus: string; emailStatus: string; accessCount?: number } | null;
  /**
   * 위임 작성 진행 상태(S35-b) — 발송됨 → 열람 → 작성 중 → 제출.
   *
   * **서버 파생값이다. 화면에서 다시 계산하지 말 것**(`report.status`와 같은 방식) — 판정은
   * shared `deriveDelegationProgress` 하나뿐이고, 여기서는 라벨만 붙인다. 특히
   * `activeDelegationToken.accessCount`로 열람을 재판정하면 안 된다: 반려 재요청은 같은
   * 토큰을 되살려 지난 라운드의 카운터가 남으므로 그 술어는 새 라운드에서 이미 참이다.
   *
   * 위임이 아니거나·해제됐거나·결재완료면 null(표시할 진행이 없다).
   *
   * ⚠️ **목록 응답은 작성 단계(DRAFT·REJECTED)에만 싣는다**(S39-c) — 제출 뒤에는 결재자 축이
   * 이어받아 두 축이 한 문서에 겹치지 않게 하기 위해서다. 상세는 제출 뒤에도 계속 싣는다
   * (헤더 배지가 "언제 제출했나"를 보여 준다). 목록 라벨 합성은 `lib/delegationProgressLabel`.
   */
  delegationProgress?: DelegationProgress | null;
  /** 현재 사용자가 다음 결재자일 때만 노출 (서버에서 userId 매칭 후 계산) */
  nextApprovalSignature?: {
    id: string;
    approvalLevel: number;
    stepLabel: string | null;
    token: string;
    /**
     * S49 릴레이 힌트 — 인앱 결재 모달(`useInAppApproval`)의 반려 안내 축. 외부 토큰 GET의
     * `delegation`과 **같은 서버 함수**(`buildDelegationRelayHint`) 산출이라 화면에서 다시
     * 판정하지 않는다. 비위임이면 null.
     */
    delegation?: { contactName: string | null; relaysComment: boolean } | null;
  } | null;
  /**
   * 3-레이어 모델 레이어1: 보고서별 결재란 정의 스냅샷.
   * Composer 결재 데이터 빌더(`composer/composerApprovalData*`) → rc 코어 `approvalLinesFromDefinition`
   * (`@safetyhub/report-composer`, 2026-09-18 R6) 경로로 소비한다.
   * sourceType='author' 단계를 포함하여 상세/인쇄 결재란 칸 수와 순서를 결정한다.
   */
  approvalLineDefinition?: ApprovalLineDefinitionRow[] | null;
  createdAt: string;
  updatedAt: string;
  site: {
    id: string;
    name: string;
    address?: string;
    constructionCost?: string;
    constructionStart?: string;
    constructionEnd?: string;
    managementNumber?: string;
    licenseNumber?: string;
    progressRate?: number;
    clientName?: string;
    clientContact?: string;
    siteManagerName?: string;
    siteManagerContact?: string;
    // Sprint 3
    constructionCategory?: string;
    company?: {
      id: string;
      name?: string;
      registrationNumber?: string;
      contact?: string;
      email?: string;
      address?: string;
    };
  };
  inspection?: {
    id: string;
    scheduledAt?: string;
    photos: Array<{
      id: string;
      url: string;
      thumbnailUrl?: string;
    }>;
    findings: Array<{
      id: string;
      description: string;
      severity: string;
      status: string;
      photoId?: string;
      category?: string;
      subCategory?: string;
      aiAnalysisDetail?: {
        location?: {
          area?: string;
          detail?: string;
        };
        riskAssessment?: {
          likelihood: number;
          likelihoodLabel?: string;
          severity: number;
          severityLabel?: string;
          riskLevel: number;
          riskLevelLabel?: string;
          action?: string;
        };
        recommendation?: {
          measures?: string[];
          legalBasis?: Array<{
            type: string;
            article: string;
            paragraphNo?: string;
            title?: string;
            summary: string;
            url?: string;
            paragraphs?: Array<{ paragraphNo: string; content: string }>;
            subItems?: Array<{ subItemNo: string; content: string | null }>;
          }>;
        };
      };
      recurrenceCount?: number;
      resolutionStatus?: string;
    }>;
  };
  author: {
    id: string;
    /** 목록 API: member.name 경로 사용. 상세 API: name 직접 사용 가능. */
    name?: string;
    email?: string;
    deletedAt?: string | null;
    memberships?: { department?: string | null; position?: string | null }[];
    /** 목록 API 응답 형태: {member: {name}} */
    member?: { name?: string | null; email?: string | null } | null;
  };
  approvals?: ReportApproval[];
  approvalSignatures?: ApprovalSignature[];
  /** 다중 점검 연결 (junction 테이블) */
  reportInspections?: Array<{
    id: string;
    inspectionId: string;
    sortOrder: number;
    inspection: {
      id: string;
      createdAt: string;
      photos: Array<{ id: string; url: string; thumbnailUrl?: string }>;
      findings: Array<{
        id: string;
        description: string;
        severity: string;
        status: string;
        photoId?: string;
        category?: string;
        subCategory?: string;
        aiAnalysisDetail?: any;
        recurrenceCount?: number;
        resolutionStatus?: string;
      }>;
      site?: { name: string; address?: string };
    };
  }>;
  /** 파생 보고서의 근거 보고서 목록 */
  derivativeSources?: Array<{
    id: string;
    sourceReportId: string;
    sourceReport?: {
      id: string;
      title: string;
      sequenceNumber?: number;
      content?: Record<string, unknown>;
      createdAt: string;
    };
  }>;
}

/**
 * 문서 content 자루 — 모든 문서가 Composer 문서라 형상은 양식(작성폼 위젯 키)이 정하고, 여기서는
 * 열린 사전(`[key: string]: unknown`)으로만 받는다. 아래 몇 필드는 구 안전점검 보고서 content의
 * 잔재 어휘로, 읽는 곳 없이 타입만 남아 있다.
 */
export interface ReportContent {
  summary?: string;
  findings?: string[];
  recommendations?: string[];
  conclusion?: string;
  /** 보고서 내 위험영역 표시 여부 (기본: true) */
  showHazardOverlay?: boolean;
  /** 보고서 내 그리기 메모 표시 여부 (기본: false) */
  showAnnotationOverlay?: boolean;
  [key: string]: unknown;
}

export interface ReportApproval {
  id: string;
  reportId: string;
  approverId: string;
  approvalLevel: number;
  status: ApprovalStatus;
  comment?: string;
  approvedAt?: string;
  approver: {
    id: string;
    name: string;
    email?: string;
  };
}

export interface ApprovalSignature {
  id: string;
  reportId: string;
  signerName: string;
  signerTitle: string;
  stepLabel?: string | null;
  approvalLevel: number;
  status: ApprovalStatus;
  signatureMethod?: SignatureMethod;
  signatureData?: string;
  signedAt?: string;
  firstViewedAt?: string | null; // 열람 시각 (최초 1회 stamp — 진행 substate 파생용)
  /**
   * 결재 요청이 **실제로 나간 시각**(토큰 발급 시점 기록, S39-b). 결재 이력의 "발송" 이벤트가
   * 이 값을 쓰고, 없으면(도입 전 행) 종전 계산(직전 단계 서명 시각)으로 폴백한다.
   */
  dispatchedAt?: string | null;
  /**
   * 담당자가 바뀌어 링크가 **다시 나간** 칸인가(서버가 `revokedToken` 존재로 계산한 불리언, S39-b).
   * 토큰 값은 어떤 응답에도 실리지 않는다 — 화면은 이 불리언만 안다.
   */
  redispatched?: boolean;
  comment?: string;
  round?: number; // 결재 라운드 (재제출 시 새 라운드 생성)
  createdAt?: string;
  // 구 결재자 프로필 FK(`approverProfileId`)는 2026-09-16 E4(approver-profile-retirement)에서 컬럼과 함께
  // 삭제됐다 — 서명행의 신원 축은 `memberId` 하나다.
  /**
   * 서명 주체 member FK(결재 차례 판정 정본 축·서버 findOne select 포함) — 공용 판정이
   * "작성자 본인이 일반 결재 칸에 서명"(제3자 아님 — 즉시 삭제) 케이스를 거르는 신원 원료.
   */
  memberId?: string | null;
  // Phase E: Member 통일 모델 — Phase B 완료 후 서버에서 함께 내려줌 (시나리오 B 호환)
  member?: {
    id: string;
    name: string;
    email?: string | null;
    phone?: string | null;
    userId?: string | null;
    user?: { deletedAt?: string | null } | null;
  } | null;
}

// DTOs
// 생성은 `createComposedReport`/`createDelegatedComposedReport`(POST /reports/composed) 둘뿐이다 —
// 아래 두 타입이 그 둘의 결재란 입력 형상이다.

/**
 * 위임 생성 시 추가 결재자 한 칸 (안전관리자 제외, level 2+).
 * 결재자의 신원은 `memberId` 하나다(구 `approverProfileId` 축은 2026-09-16 E4에서 삭제) —
 * 담당자 미지정 칸(`assigneeUnset`)이 아니라면 이 값이 있어야 서버가 단계를 해소한다.
 */
export interface DelegationApprovalLineInput {
  memberId?: string;
  approvalLevel: number;
  label?: string;
  /** 통지 채널 — **정본 축**(2026-08-23 단일화. 구 객체 축 notifyChannels는 서버가 호환 수용만 한다). */
  notifyVia?: ('sms' | 'email')[];
  /**
   * 담당자 미지정 칸(오프라인 수기) — 칸만 만들고 담당자는 비운다.
   * 이 마커가 없으면 서버가 "빈 행"으로 보고 칸을 통째로 잃는다
   * (결정 2026-08-13-approval-slot-assignee-separation).
   */
  assigneeUnset?: boolean;
}

/**
 * P1: 작성자 추가 결재란 한 칸 (메타 base 이후 level N+1..).
 * maxAdditionalLines 한도 내에서 서버가 검증.
 */
export interface AdditionalApprovalLineInput {
  memberId?: string;
  label?: string;
  /** 통지 채널 — **정본 축**(2026-08-23 단일화. 구 객체 축 notifyChannels는 서버가 호환 수용만 한다). */
  notifyVia?: ('sms' | 'email')[];
}

export interface CreateReportResponse {
  report: Report;
  draftToken?: {
    id: string;
    token: string;
    expiresAt: string;
    smsStatus: string;
    emailStatus: string;
  };
}

export interface UpdateReportDto {
  title?: string;
  content?: ReportContent;
}

export interface SubmitApprovalDto {
  comment?: string;
}

export interface ApprovalActionDto {
  action: 'APPROVE' | 'REJECT';
  signerName: string;
  signerTitle: string;
  signatureMethod: SignatureMethod;
  signatureData?: string;
  comment?: string;
}

export interface ReportsFilters {
  siteId?: string;
  /** 현재 그룹 필터 — 그 그룹 소속 현장의 문서만. 생략 시 접근 가능한 전체 그룹. */
  groupId?: string | null;
  /**
   * 상태 필터 — 단일 또는 집합(서버가 `status=A,B` 콤마 직렬화를 받는다, W4d). 집합은
   * '초안' 탭이 DRAFT+CREATING 두 상태를 스코프로 갖게 되어서다(`documentTabs`의
   * `expandStatusQueryScope`가 정본 — 화면은 단일 상태 state를 유지하고 질의만 넓힌다).
   */
  status?: ReportStatus | ReportStatus[];
  /**
   * 양식(템플릿) 필터 — `report_templates.key`. 문서관리 목록의 「유형」 축이 이것이다.
   *
   * 모든 문서가 `COMPOSED`라 저장용 `type` 컬럼은 더 이상 유형을 가르지 못한다 — 사람이
   * 「유형」이라 부르는 것은 양식 이름이고(배지·제목이 이미 `templateName`을 보여 준다),
   * 그래서 필터도 같은 축을 쓴다. 서버 `GET /reports?templateKey=`(등호 AND)와 짝.
   */
  templateKey?: string;
  inspectionId?: string;
  /** 제목·작성자 표시명·현장명 부분일치 (대소문자 무시) */
  search?: string;
  /** true면 내가 작성한 문서만 */
  mine?: boolean;
  limit?: number;
  offset?: number;
}

export interface ReportsListResponse {
  data: Report[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

// ===================================
// 결재 대기 목록 관련
// ===================================

export interface PendingApprovalItem {
  report: {
    id: string;
    title: string;
    /**
     * Composer 양식 이름 (서버가 `templateKey`로 조인해 채운다).
     * 유형 배지가 보여줄 사람이 읽는 이름 — `getReportTypeLabel` 인자.
     */
    templateName?: string | null;
    status: ReportStatus;
    createdAt: string;
    site: {
      id: string;
      name: string;
      address?: string;
    };
    author: {
      id: string;
      /** 목록 API 응답 형태: member.name 경로. */
      name?: string;
      email?: string;
      deletedAt?: string | null;
      member?: { name?: string | null; email?: string | null } | null;
    };
    authorSignerName?: string | null;
    /** 보고서 생성 시점 작성자 이름 스냅샷 (authorSignerName 다음 우선순위). */
    authorSnapshotName?: string | null;
    /** 작성자 서명 시각 — 작성자 단계는 ApprovalSignature 행이 없어 이 컬럼이 유일한 소스. */
    authorSignedAt?: string | null;
    /** 작성자 서명 시점 직위 스냅샷. */
    authorSignerTitle?: string | null;
    /**
     * 결재란 정의 스냅샷(레이어1). 아직 서명행이 만들어지지 않은 단계까지 포함하므로
     * 결재 진행 스텝퍼의 노드 베이스가 된다. Report.approvalLineDefinition과 같은 형태.
     */
    approvalLineDefinition?: ApprovalLineDefinitionRow[] | null;
  };
  signatures: ApprovalSignature[];
  currentPendingSignature: ApprovalSignature | null;
  totalLevels: number;
  completedLevels: number;
}

/**
 * 결재함 목록 범위 — 탭이 곧 목록 범위의 정본이고, 통계 카드는 대응 탭으로 가는 바로가기다.
 * 서버 `APPROVAL_INBOX_VIEWS`와 값이 1:1로 맞아야 400(허용값 검증)을 맞지 않는다.
 *
 * `processed-today`는 카드=필터 시절의 범위라 지금 화면에서는 쓰지 않지만, 서버가 계속
 * 받는 값이라 계약으로 남긴다(탭이 쓰는 부분집합은 `ApprovalInboxTabView`).
 */
export type ApprovalInboxView =
  | 'pending'
  | 'submitted'
  | 'processed-today'
  | 'processed'
  | 'rejected';

export interface PendingApprovalsFilters {
  siteId?: string;
  /** 현재 그룹 필터 — 그 그룹 소속 현장의 결재 건만. */
  groupId?: string | null;
  myOnly?: boolean;
  limit?: number;
  offset?: number;
  /** 생략 시 서버 기본값 pending(전체 결재 대기). */
  view?: ApprovalInboxView;
}

export interface PendingApprovalsResponse {
  data: PendingApprovalItem[];
  meta: {
    total: number;
    limit: number;
    offset: number;
  };
}

export async function getPendingApprovals(
  companyId: string,
  filters?: PendingApprovalsFilters
): Promise<PendingApprovalsResponse> {
  const params = new URLSearchParams();
  params.append('companyId', companyId);

  if (filters?.siteId) params.append('siteId', filters.siteId);
  if (filters?.groupId) params.append('groupId', filters.groupId);
  if (filters?.myOnly) params.append('myOnly', 'true');
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.offset) params.append('offset', String(filters.offset));
  if (filters?.view) params.append('view', filters.view);

  const response = await api.get<ApiResponse<PendingApprovalItem[]> & { meta: PendingApprovalsResponse['meta'] }>(
    `/reports/pending-approvals?${params.toString()}`
  );

  return {
    data: response.data.data || [],
    meta: response.data.meta || { total: 0, limit: 20, offset: 0 },
  };
}

/**
 * 결재함 탭 4종의 배지 카운트 (서버 `ApprovalInboxTabCounts`와 동일 형태).
 * 카드(내 기준)와 달리 조직/현장 전체 범위다 — submitted만 "내가 작성"이라 mySubmitted와 같다.
 */
export interface ApprovalInboxTabCounts {
  pending: number;
  submitted: number;
  processed: number;
  rejected: number;
}

/**
 * 탭이 쓰는 view — 키 이름이 곧 view 값이라 `tabCounts[view]`로 배지를 집을 수 있다.
 * `Extract`로 뽑아 두면 서버 view 목록에서 값이 빠질 때 여기서 먼저 타입이 깨진다.
 */
export type ApprovalInboxTabView = Extract<ApprovalInboxView, keyof ApprovalInboxTabCounts>;

/** 결재함 통계 카드 4종 + 탭 배지 (서버 `ApprovalStats`와 동일 형태). */
export interface ApprovalStats {
  /** 내가 현재 결재자인 대기 건수. */
  myPending: number;
  /** 내가 작성자이고 결재 진행 중(PENDING_APPROVAL)인 보고서 수. */
  mySubmitted: number;
  /** 오늘 내가 승인·반려한 건수. */
  processedToday: number;
  /** 어제 하루 내가 승인·반려한 건수 — '오늘 처리 완료'의 전날 대비 증감에만 쓴다. */
  processedYesterday: number;
  /** 최근 30일 최종 승인본의 제출→승인 평균 시간. 대상 0건이면 null. */
  avgDurationHours: number | null;
  /** 탭 배지 — 카드와 달리 조직 전체 범위. */
  tabCounts: ApprovalInboxTabCounts;
}

export async function getApprovalStats(
  companyId: string,
  filters?: { siteId?: string; groupId?: string | null }
): Promise<ApprovalStats> {
  const params = new URLSearchParams();
  params.append('companyId', companyId);
  if (filters?.siteId) params.append('siteId', filters.siteId);
  if (filters?.groupId) params.append('groupId', filters.groupId);

  const response = await api.get<ApiResponse<ApprovalStats>>(
    `/reports/approval-stats?${params.toString()}`
  );

  return (
    response.data.data ?? {
      myPending: 0,
      mySubmitted: 0,
      processedToday: 0,
      processedYesterday: 0,
      avgDurationHours: null,
      tabCounts: { pending: 0, submitted: 0, processed: 0, rejected: 0 },
    }
  );
}

// ===================================
// 보고서 API
// ===================================

/** 문서관리 통계 카드 + 탭 배지 (서버 `DocumentStats`와 동일 형태). */
export interface DocumentStats {
  /** 전체 문서 (취소본 제외 — 목록 기본 범위와 같다). */
  total: number;
  /** 작성 중 — DRAFT + CREATING(초안 탭 집합, 결정 2026-09-06 ❓4 — 서버 주석 참조). */
  draft: number;
  /** 결재 진행 중(PENDING_APPROVAL). */
  pendingApproval: number;
  /** 결재 완료(APPROVED). */
  approved: number;
  /** 내가 작성한 문서 — '내가 작성' 탭 배지. */
  mine: number;
  /** 이번 달 신규 작성. */
  createdThisMonth: number;
  /** 이번 달 신규 vs 지난달 증감률(정수 %). 지난달 0건이면 null. */
  createdChangePercent: number | null;
  /** 최근 7일 결재 완료. */
  approvedLast7Days: number;
  /** 최근 7일 승인 vs 그 이전 7일 증감률(정수 %). 이전 7일 0건이면 null. */
  approvedChangePercent: number | null;
}

export async function getDocumentStats(
  companyId: string,
  filters?: { siteId?: string; groupId?: string | null }
): Promise<DocumentStats> {
  const params = new URLSearchParams();
  params.append('companyId', companyId);
  if (filters?.siteId) params.append('siteId', filters.siteId);
  if (filters?.groupId) params.append('groupId', filters.groupId);

  const response = await api.get<ApiResponse<DocumentStats>>(
    `/reports/document-stats?${params.toString()}`
  );

  return (
    response.data.data ?? {
      total: 0,
      draft: 0,
      pendingApproval: 0,
      approved: 0,
      mine: 0,
      createdThisMonth: 0,
      createdChangePercent: null,
      approvedLast7Days: 0,
      approvedChangePercent: null,
    }
  );
}

// API Functions
export async function getReports(
  companyId: string,
  filters?: ReportsFilters
): Promise<ReportsListResponse> {
  const params = new URLSearchParams();
  params.append('companyId', companyId);

  if (filters?.siteId) params.append('siteId', filters.siteId);
  if (filters?.groupId) params.append('groupId', filters.groupId);
  // 집합은 콤마 직렬화 — 서버 `parseStatusFilter`가 콤마·반복 param 둘 다 받지만 URL이
  // 짧은 쪽으로 통일한다. 빈 배열은 필터 없음(append 자체를 안 한다).
  if (filters?.status && filters.status.length > 0) {
    params.append(
      'status',
      Array.isArray(filters.status) ? filters.status.join(',') : filters.status,
    );
  }
  if (filters?.templateKey) params.append('templateKey', filters.templateKey);
  if (filters?.inspectionId) params.append('inspectionId', filters.inspectionId);
  if (filters?.search?.trim()) params.append('search', filters.search.trim());
  if (filters?.mine) params.append('mine', 'true');
  if (filters?.limit) params.append('limit', String(filters.limit));
  if (filters?.offset) params.append('offset', String(filters.offset));

  const response = await api.get<ApiResponse<Report[]> & { meta: ReportsListResponse['meta'] }>(
    `/reports?${params.toString()}`
  );

  return {
    data: response.data.data || [],
    meta: response.data.meta || { total: 0, limit: 20, offset: 0 },
  };
}

export async function getReport(id: string, companyId: string): Promise<Report> {
  const response = await api.get<ApiResponse<Report>>(
    `/reports/${id}?companyId=${companyId}`
  );
  return response.data.data!;
}

/**
 * Composer 템플릿 기반 보고서 작성 (Phase 4a-2).
 * POST /reports/composed → Report(templateKey 저장) 반환.
 */
export async function createComposedReport(input: {
  companyId: string;
  siteId: string;
  templateKey: string;
  title: string;
  content: Record<string, unknown>;
  /** 보고서가 앵커할 점검 ID(들). 단일 점검 기반 생성 시 1개. */
  inspectionIds?: string[];
  additionalApprovalLines?: AdditionalApprovalLineInput[];
}): Promise<Report> {
  const response = await api.post<ApiResponse<Report>>('/reports/composed', input);
  return response.data.data!;
}

/**
 * Composer 템플릿 위임 작성 (delegationMeta.enabled 템플릿 전용, DLR 위임 동형).
 * POST /reports/composed (delegationType=DELEGATED) → { report, draftToken } 반환.
 * content는 서버가 생성 시점에 prefill(previewResolveByContext)로 박제하므로 빈 객체를 전송한다.
 * (결정: docs/decisions/2026-07-06-composer-report-delegation-design.md)
 */
export async function createDelegatedComposedReport(input: {
  companyId: string;
  siteId: string;
  templateKey: string;
  title: string;
  /** 앵커 점검 ID(들). 단일 점검 기반 위임 시 1개. */
  inspectionIds?: string[];
  delegatedContactId: string;
  notifyChannels: ('sms' | 'email')[];
  /**
   * 진입 전 사용자 입력(위임 시점 위임자 지시문, trim 후 빈 값 생략) — 서버가 인증 위임자 컨텍스트로
   * guidance 사전 생성에 동봉(동일성 트리거·캐시 저장은 직접 흐름과 동일). 2026-07-26 위임 흐름 배선.
   */
  userGuidance?: string;
  /** 위임자(안전관리자 level 1) 이후 추가 결재자 (level 2+) */
  delegationApprovalLines?: DelegationApprovalLineInput[];
}): Promise<CreateReportResponse> {
  const response = await api.post<ApiResponse<CreateReportResponse>>('/reports/composed', {
    ...input,
    delegationType: 'DELEGATED',
    content: {},
  });
  const result = response.data.data!;
  if (result && 'report' in result) {
    return result;
  }
  return { report: result as unknown as Report };
}

export async function updateReport(
  id: string,
  data: UpdateReportDto,
  companyId: string
): Promise<Report> {
  const response = await api.patch<ApiResponse<Report>>(
    `/reports/${id}?companyId=${companyId}`,
    data
  );
  return response.data.data!;
}

export async function deleteReport(id: string, companyId: string): Promise<void> {
  await api.delete(`/reports/${id}?companyId=${companyId}`);
}

// ─── P1: 결재라인 미리보기 API ────────────────────────────

/**
 * 미리보기 응답 계약은 shared가 정본이다(`packages/shared/src/types/approval-line.ts` — 칸별 JSDoc도 거기).
 * 종전엔 여기 손복사 미러를 두어 한쪽만 고치면 tsc가 눈을 감았다(2026-09-09 연락처 세 칸이 그렇게 샜다).
 * 라운드 6에서 re-export로 접었다 — 기존 import 경로(`lib/reports`)는 그대로 살아 있다.
 */
export type { ApprovalLinePreviewRow, ApprovalLinePreviewData } from '@safetyhub/shared';

/**
 * 결재라인 미리보기 — 템플릿 `approvalLineMeta` 기반, `templateKey`로 조회
 * (`GET /report-composer/approval-line-preview/:templateKey`). approvalLineMeta 없는 템플릿은
 * hasApprovalLinePolicy:false. 유형 축으로 조회하는 갈래는 없다(모든 문서가 Composer 문서다).
 *
 * @param delegatedToMemberId 위임 대상 Member — `{type:'delegatedTo'}` 단계를 그 사람으로 해소한다
 *   (2026-09-09). 서버는 처음부터 이 쿼리를 받았는데 **넘기는 호출부가 하나도 없어** 위임 모달에서
 *   담당자를 골라도 결재란 1행(체인 `위임 담당자|작성자`)이 작성자로 남았다.
 */
export async function fetchComposedApprovalLinePreview(
  templateKey: string,
  siteId: string,
  delegatedToMemberId?: string,
): Promise<ApprovalLinePreviewData> {
  const params = new URLSearchParams({ siteId });
  if (delegatedToMemberId) params.set('delegatedToMemberId', delegatedToMemberId);
  const response = await api.get<ApiResponse<ApprovalLinePreviewData>>(
    `/report-composer/approval-line-preview/${encodeURIComponent(templateKey)}?${params.toString()}`,
  );
  return (
    response.data.data ?? {
      baseRows: [],
      maxAdditionalLines: 0,
      hasApprovalLinePolicy: false,
      baseNonLockedCount: 0,
    }
  );
}

// ─── 삭제 승인 요청 ────────────────────────────

export interface DeleteRequestInfo {
  id: string;
  /** PENDING(삭제 승인 대기) | REJECTED(삭제 요청 거절됨) */
  status: 'PENDING' | 'REJECTED';
  reason: string;
  approverName: string;
  createdAt: string;
  /** REJECTED일 때만: 거절 사유(결재자 코멘트) */
  resolverComment?: string | null;
  /** REJECTED일 때만: 거절 처리 시각 */
  resolvedAt?: string | null;
  requester: { name: string };
}

export interface DeleteRequestResult {
  requestId: string;
  approverName: string;
  hasContact: boolean;
}

export type DeleteRequestChannel = 'sms' | 'email';

export async function requestReportDelete(
  id: string,
  companyId: string,
  reason: string,
  channels?: DeleteRequestChannel[],
): Promise<DeleteRequestResult> {
  const res = await api.post<{ success: boolean; data: DeleteRequestResult }>(
    `/reports/${id}/delete-request?companyId=${companyId}`,
    { reason, ...(channels ? { channels } : {}) },
  );
  return res.data.data;
}

export async function cancelReportDeleteRequest(
  id: string,
  companyId: string,
): Promise<void> {
  await api.delete(`/reports/${id}/delete-request?companyId=${companyId}`);
}

export async function getReportDeleteRequest(
  id: string,
  companyId: string,
): Promise<DeleteRequestInfo | null> {
  const res = await api.get<{ success: boolean; data: DeleteRequestInfo | null }>(
    `/reports/${id}/delete-request?companyId=${companyId}`,
  );
  return res.data.data;
}

// ─── 삭제 승인 (토큰 기반, 공개 API) ────────────

export interface DeleteApprovalInfo {
  id: string;
  type: string;
  reason: string;
  requesterName: string;
  approverName: string;
  createdAt: string;
  /**
   * 외부 결재·현장담당자 서명과 같은 공개 계약(`ExternalSignatureReportView`) — 서버 `report-delete.service.
   * getDeleteRequestByToken`이 조립. 같은 계약을 여기 손으로 다시 선언하지 말 것 — shared 타입 하나다.
   */
  report: ExternalSignatureReportView;
}

export async function getDeleteApproval(token: string): Promise<DeleteApprovalInfo> {
  const res = await api.get<{ success: boolean; data: DeleteApprovalInfo }>(
    `/delete-approval/${token}`,
    { skipAuth: true } as any,
  );
  return res.data.data;
}

export async function processDeleteApproval(
  token: string,
  action: 'APPROVE' | 'REJECT',
  comment?: string,
): Promise<void> {
  await api.post(`/delete-approval/${token}`, { action, comment }, { skipAuth: true } as any);
}

// 제출 body의 `customApprovalLines`(구 결재자 프로필 기반 추가 결재선 — `CustomApprovalLine`)는 2026-09-16
// E4(approver-profile-retirement)에서 서버 경로와 함께 삭제했다. web은 이미 보내지 않았고(항상 undefined),
// 결재선은 제출 전 `setReportApprovalLines`로만 정한다.

export interface AuthorSignDto {
  signerName: string;
  signerTitle?: string;
  signatureData: string; // Base64 인코딩된 서명 이미지
}

// (기여자 서명 클라이언트는 없다 — 서버에도 그 엔드포인트가 없다.)

/**
 * 작성자 서명
 * 결재 제출 전 작성자(주 담당자)가 보고서에 서명합니다.
 */
export async function authorSign(
  id: string,
  companyId: string,
  data: AuthorSignDto
): Promise<Report> {
  const response = await api.post<ApiResponse<Report>>(
    `/reports/${id}/author-sign?companyId=${companyId}`,
    data
  );
  return response.data.data!;
}

/**
 * 완료자 서명(F-45) — 위임 문서를 결재 없이 「완료」하는 제출에서만 싣는다. 서버는 「완료형 ∧
 * 위임」이면 필수(400 `REPORT_COMPLETER_SIGNATURE_REQUIRED`), 아니면 와서는 안 된다
 * (400 `REPORT_COMPLETER_SIGNATURE_NOT_APPLICABLE`). 판정은 `useReportApproval`의
 * `requiresCompleterSign`이 서버와 같은 술어로 한다.
 */
export interface CompleterSignatureInput {
  signatureData: string;
  signerName: string;
  signerTitle?: string;
}

export async function submitForApproval(
  id: string,
  companyId: string,
  comment?: string,
  signatureMode?: ApprovalSignatureMode,
  extra?: { completerSignature?: CompleterSignatureInput },
): Promise<Report> {
  const response = await api.post<ApiResponse<Report>>(
    `/reports/${id}/submit?companyId=${companyId}`,
    {
      comment,
      signatureMode,
      // 대상이 아니면 키 자체를 싣지 않는다 — `undefined`는 JSON에서 빠지지만 의도를 명시한다.
      ...(extra?.completerSignature ? { completerSignature: extra.completerSignature } : {}),
    }
  );
  return response.data.data!;
}

/**
 * 제출 취소 (결재가 진행되기 전에만 가능)
 */
export async function cancelSubmission(
  id: string,
  companyId: string
): Promise<Report> {
  const response = await api.post<ApiResponse<Report>>(
    `/reports/${id}/cancel-submission?companyId=${companyId}`,
    {}
  );
  return response.data.data!;
}

/**
 * 결재 알림 재전송
 */
export async function resendApprovalNotification(
  reportId: string,
  companyId: string
): Promise<{ success: boolean; approverName: string }> {
  const response = await api.post<ApiResponse<{ success: boolean; approverName: string }>>(
    `/reports/${reportId}/resend-approval?companyId=${companyId}`
  );
  return response.data.data!;
}

export async function processApproval(
  reportId: string,
  signatureId: string,
  data: ApprovalActionDto,
  companyId: string
): Promise<Report> {
  const response = await api.post<ApiResponse<Report>>(
    `/reports/${reportId}/signatures/${signatureId}/process?companyId=${companyId}`,
    data
  );
  return response.data.data!;
}

// Helpers

// 저장용 유형 축은 없다 — 은퇴 유형 이름 맵도 두지 않는다(그 문자열이 화면에 흘러들어올 통로가
// 없다). 문서의 종류는 양식(`templateKey`/`templateName`)이고 표시 계층은 양식 이름만 안다.

/** 양식 이름을 못 받았을 때의 중립 라벨(코드값·키를 화면에 흘리지 않는다). */
const COMPOSED_FALLBACK_LABEL = '양식 보고서';

// 문서관리 목록의 「유형」 필터 선택지 상수를 두지 않는다 — 선택지는 **활성 Composer 양식**에서
// 파생한다(`pages/reports/documentTemplateFilter.ts`, 질의는 위 `ReportsFilters.templateKey`).
// 유형을 코드에 다시 열거하지 말 것 — 운영자가 저작하는 양식은 코드가 알 수 없다.

/**
 * 문서 유형 라벨 = 양식(ReportTemplate) 이름.
 *
 * 목록·결재함·추이 API가 `templateKey`로 조인해 내려주는 `templateName`을 그대로 보여 준다.
 *
 * ⚠️ **어떤 경우에도 코드값·양식 키를 그대로 반환하지 않는다.** 예전 `labels[type] || type`
 * 폴백 탓에 결재함·문서관리 배지에 `COMPOSED` 문자열이 그대로 노출됐다(2026-07-27).
 * 이름이 비어 오면(양식 행 삭제·옛 응답) 화면에는 중립 라벨이 나간다.
 */
export function getReportTypeLabel(templateName?: string | null): string {
  const trimmed = templateName?.trim();
  return trimmed || COMPOSED_FALLBACK_LABEL;
}

// 유형별 tone 맵을 두지 않는다 — 현역 저장용 유형이 하나도 없다.
// 색은 `getReportTypeBadgeTone`이 한 값(brand)으로 정한다. 양식별로 색을 가르고 싶다면 그건
// 유형 축이 아니라 양식 축의 일이다(추이 차트가 `templateKey` 정렬 인덱스로 하듯이).

/** 문서 상태 → tone 단일 정본. */
const DOC_STATUS_TONE: Record<ReportStatus, BadgeTone> = {
  CREATING: 'amber',
  DRAFT: 'slate',
  PENDING_APPROVAL: 'amber',
  APPROVED: 'emerald',
  REJECTED: 'rose',
  // 취소는 반려(rose)와 다른 축이다 — 되돌릴 판정이 아니라 중단이라 중립색을 쓴다
  // (`organizations`·`TenantMergeSection`·일정 목록이 이미 CANCELLED를 slate로 그린다).
  CANCELLED: 'slate',
};

// 칩 클래스 다리를 두지 않는다 — 상태 색은 `getDocStatusBadgeTone`(Badge tone) 하나와
// 아래 점(dot) 파생뿐이다.

/** tone → 점(dot) 텍스트 색. 중립(slate)만 한 단계 옅게 둔다(초안 표시 관행). */
const TONE_DOT_TEXT_CLASS: Record<BadgeTone, string> = {
  blue: 'text-blue-500',
  emerald: 'text-emerald-500',
  amber: 'text-amber-500',
  orange: 'text-orange-500',
  rose: 'text-rose-500',
  violet: 'text-violet-500',
  teal: 'text-teal-500',
  slate: 'text-ink-400',
  brand: 'text-brand-500',
};

// 상태 → 라벨은 `lib/reportStatusLabel.resolveReportStatusLabel(report)` 한 벌이다 — enum만 보던
// `getReportStatusLabel`/`getDocStatusLabel`은 2026-08-30 리뷰 INFO로 접었다(raw enum 폴백 + 2벌).
// 이 모듈은 vi.mock 대상이라 순수 라벨 술어를 두지 않는다(그 모듈 헤더 참조).

/** 상태별 작은 색 점(dot) Tailwind 색상 클래스. 인라인 원형 배지 등에 사용. */
export function getReportStatusDotColor(status: ReportStatus): string {
  return TONE_DOT_TEXT_CLASS[DOC_STATUS_TONE[status] ?? 'slate'];
}

/**
 * 결재 진행 단계별 substate 파생 (2층 모델, 2026-06-11)
 *
 * report.status === 'PENDING_APPROVAL'일 때, 현재 round의 ApprovalSignature 배열로부터
 * "현재 활성 단계"와 그 substate를 파생한다.
 *
 * substate:
 *   - 'approved-step': 현재 활성 단계가 승인됨(다음 단계 대기 또는 전체 완료 직전)
 *   - 'viewed'       : 열람됨(firstViewedAt 있음) + 미결재
 *   - 'sent'         : 발송됨(행 존재) + 미열람·미결재
 *   (구 'approving'(결재 중)은 생산·소비처가 없던 죽은 멤버라 2026-08-30 리뷰 INFO로 제거 —
 *    "열람 후 미결재"는 'viewed'가 곧 그 상태다.)
 *
 * 양식 결재란(print/PDF)에는 사용하지 말 것 — 헤더 배지·chrome 전용.
 */
export type ApprovalProgressSubstate =
  | 'approved-step'
  | 'viewed'
  | 'sent'
  /**
   * 담당자가 해소되지 않아 **아무에게도 나가지 않은** 칸(S39-b·대기 칸). `sent`와 나란히 두는
   * 이유: 행은 존재하지만 발송은 없었다 — 행 존재를 곧 발송으로 읽으면 화면이 "발송됨"이라고
   * 거짓말을 한다(2026-08-30 목록 배지 실사고).
   */
  | 'parked';

export interface ApprovalProgressResult {
  /** 현재 활성 approvalLevel (미서명 최저 단계). 전체 완료 시 null */
  activeLevel: number | null;
  substate: ApprovalProgressSubstate | null;
  /**
   * 헤더 배지·목록 상태 배지에 표시할 라벨 — `{단계명} {발송됨/열람됨}`.
   * 대기 칸(담당자 미해소)이면 발송이 없었으므로 `{단계명} · 담당자 배정 대기`로 끝낸다.
   */
  label: string;
  /**
   * 활성 단계의 **담당자 이름**(S39-b). 단계 라벨이 "안전관리자"여도 지금 그 칸을 쥔 사람이
   * 누구인지는 다른 축이다. 대기 칸(담당자 미해소)이면 `null`, 활성 단계가 없어도 `null`.
   */
  activeSignerName: string | null;
  /**
   * 사람 이름까지 곁들인 상세 라벨(S39-b) — `{단계명} · {이름} {발송됨/열람됨}`.
   * 단계명이 곧 이름이면(일반 "결재" 칸) `label`과 같다. 결재 이력 모달 상단 배지 전용 —
   * 목록 배지(`resolveApprovalProgressLabel`)는 `label`만 쓰므로 사람 이름이 목록으로 새지 않는다.
   * 단 **대기 칸 문구는 두 라벨이 같다** — 발송 여부는 상세·목록이 갈릴 축이 아니다.
   */
  detailLabel: string;
}

/** 담당자/작성자(author) 단계 라벨 — 결재 진행/번호에서 제외 대상 */
export function isAuthorApprovalStep(stepLabel?: string | null): boolean {
  const label = stepLabel?.trim();
  return label === '담당자' || label === '작성자';
}

/**
 * 결재 단계명 표시값 — 결재라인 단계 라벨(stepLabel) 우선.
 * 일반 라벨("결재")이거나 비어 있으면 결재자 이름으로 폴백.
 */
export function approvalStepDisplayName(sig: {
  stepLabel?: string | null;
  signerName?: string | null;
}): string {
  const label = sig.stepLabel?.trim();
  if (label && label !== '결재' && !isAuthorApprovalStep(label)) return label;
  return sig.signerName?.trim() || label || '';
}

export function deriveApprovalProgress(
  signatures: (Pick<
    ApprovalSignature,
    'approvalLevel' | 'round' | 'status' | 'signedAt' | 'firstViewedAt' | 'signerName' | 'stepLabel'
  > &
    // 대기 칸 판정 축(S39-b `activeSignerName`) — 목록 응답처럼 안 실은 경우도 있어 선택.
    // `member`는 이름 폴백 축(`approvalSlotActorName`) — 상세 응답만 싣는다.
    Partial<Pick<ApprovalSignature, 'memberId' | 'member'>>)[],
): ApprovalProgressResult {
  if (!signatures.length) {
    return {
      activeLevel: null,
      substate: null,
      label: '결재 대기',
      activeSignerName: null,
      detailLabel: '결재 대기',
    };
  }

  // 차수(round): 1차 제출 → 반려 후 재제출 시 2차, 3차... 한 차수 안에서 결재가 진행돼도
  // 차수는 유지되고 표시 이름만 다음 결재 단계로 넘어간다.
  const inRound = latestRoundSignatures(signatures)
    .sort((a, b) => a.approvalLevel - b.approvalLevel);
  // 차수 접미(「(N차)」)용 — 최신 round 값 자체는 필터가 돌려주지 않으므로 그 행에서 읽는다.
  // 읽는 규칙은 `signatureRound` 한 벌(결재 이력 그룹핑과 같은 자 — shared `roundOf`와 같은 기본값).
  const latestRound = inRound[0] ? signatureRound(inRound[0]) : 1;

  // 현재 활성 결재 위치: 미서명(PENDING) 중 approvalLevel이 가장 낮은 단계.
  // 담당자/작성자 단계는 제출 시 이미 승인되므로 자연히 건너뛴다.
  const firstPending = inRound.find((s) => s.status === 'PENDING');

  if (!firstPending) {
    // 현재 차수 전체 완료 — report.status가 APPROVED로 전이
    return {
      activeLevel: null,
      substate: 'approved-step',
      label: '결재 완료',
      activeSignerName: null,
      detailLabel: '결재 완료',
    };
  }

  // "{결재단계명} {발송됨/열람됨}" + (2차부터) " (N차)"
  const name = approvalStepDisplayName(firstPending);
  const namePart = name ? `${name} ` : '';
  const roundSuffix = latestRound >= 2 ? ` (${latestRound}차)` : '';
  const word = firstPending.firstViewedAt ? '열람됨' : '발송됨';
  // 대기 칸(담당자 미해소)은 **행만 있고 발송은 없었다**. 이 판정이 label·detailLabel·substate
  // 셋을 함께 가른다 — 예전엔 detailLabel만 접어서, 같은 칸을 상세는 "배정 대기"로 목록은
  // "발송됨"으로 불렀다(2026-08-30 실사고).
  const isParked = isApprovalSignatureParked(firstPending);
  const substate: ApprovalProgressSubstate = isParked
    ? 'parked'
    : firstPending.firstViewedAt
      ? 'viewed'
      : 'sent';
  // 담당자 이름 축(S39-b) — 대기 칸이면 이름이 없다(플레이스홀더를 사람 이름처럼 보이지 않게).
  // 사다리는 결재 이력과 같은 한 벌(`approvalSlotActorName`) — 여기만 따로 적으면 갈린다.
  const activeSignerName = approvalSlotActorName(firstPending);
  // 대기 칸은 아무에게도 나가지 않았으니 "발송됨" 대신 배정 대기 문구로 끝낸다.
  const parkedLabel = `${name ? `${name} · ` : ''}${PARKED_SLOT_DISPLAY_NAME}${roundSuffix}`;
  const sentLabel = `${namePart}${word}${roundSuffix}`;
  const detailLabel = isParked
    ? parkedLabel
    : activeSignerName
      ? // 단계명이 곧 이름(일반 "결재" 칸은 signerName으로 폴백)이면 같은 말을 두 번 하지 않는다.
        `${name && name !== activeSignerName ? `${name} · ` : ''}${activeSignerName} ${word}${roundSuffix}`
      : // 대기 칸이 아닌데 이름이 비었을 뿐이면 배정 대기가 아니다 — 단계명만으로 말한다.
        sentLabel;
  return {
    activeLevel: firstPending.approvalLevel,
    substate,
    label: isParked ? parkedLabel : sentLabel,
    activeSignerName,
    detailLabel,
  };
}

/**
 * 상태 배지에 곁들일 결재 진행 substate 문구 (S39) — 없으면 `null`.
 *
 * 목록 행이 `PENDING_APPROVAL`을 '결재 대기'로만 보여 주면 2단까지 끝나고 3단이 도는 문서와
 * 아무도 열어 보지 않은 문서가 화면에서 구별되지 않는다. 문구는 `deriveApprovalProgress`가
 * 소유한다 — 결재 이력 모달이 `현재: {label}`로 쓰는 그 문자열을 그대로 쓰고, 여기서 새 어휘를
 * 만들지 않는다(같은 진행을 두 낱말로 부르면 어느 쪽이 정본인지 사라진다).
 *
 * `null`로 접는 경우:
 * - `PENDING_APPROVAL`이 아니다 — 진행 중이 아닌 문서에 진행 문구를 붙일 자리가 없다.
 * - 서명 행이 없다 / 응답이 필드를 안 실었다 — 파생 축이 없으면 모른다고 말하는 편이 맞다.
 * - 활성 단계가 없다(전 칸 서명 완료·상태 전이 직전) — 라벨이 '결재 완료'라, 배지의
 *   '결재 대기'와 나란히 두면 서로 모순된 문장이 된다.
 */
export function resolveApprovalProgressLabel(report: {
  status: string;
  approvalSignatures?:
    | (Pick<
        ApprovalSignature,
        'approvalLevel' | 'round' | 'status' | 'signedAt' | 'firstViewedAt' | 'signerName' | 'stepLabel'
      > &
        // 대기 칸 판정 축 — `deriveApprovalProgress`와 같은 형상이라야 목록도 "발송됨"과
        // "담당자 배정 대기"를 가른다(선택: 목록 응답이 FK를 안 실어도 이름 축으로 판정된다).
        Partial<Pick<ApprovalSignature, 'memberId'>>)[]
    | null;
}): string | null {
  if (report.status !== 'PENDING_APPROVAL') return null;
  const signatures = report.approvalSignatures;
  if (!signatures?.length) return null;

  const progress = deriveApprovalProgress(signatures);
  if (progress.activeLevel === null || progress.substate === null) return null;
  return progress.label;
}

/** 상태 → 공용 Badge tone (목업 `.status` 톤). 정본은 DOC_STATUS_TONE. */
export function getDocStatusBadgeTone(status: ReportStatus): BadgeTone {
  return DOC_STATUS_TONE[status] ?? 'slate';
}

/**
 * 보고서 유형 배지의 공용 Badge tone (목업 `.type-pill`).
 *
 * 모든 문서가 우리 시스템 양식(Composer)이라 한 값 brand다 — 유형·상태 어느 축에서도 쓰지 않던
 * 빈 슬롯이라 다른 값과 겹치지 않고, 배지에 실제 양식명이 나오는데 중립색이면 "미분류"처럼
 * 읽힌다. 유형별 tone 맵은 없다(저장용 유형 축 자체가 없다).
 */
export function getReportTypeBadgeTone(): BadgeTone {
  return 'brand';
}


/** TBM 진행 시간 계산 (분 단위 문자열, 예: "12분", "1시간 5분") */
export function getTBMDuration(report: Report): string | null {
  const startedAt = report.qrSessions?.[0]?.createdAt;
  if (!startedAt) return null;

  const start = new Date(startedAt).getTime();
  const end = report.completedAt
    ? new Date(report.completedAt).getTime()
    : Date.now(); // 아직 완료 안 됐으면 현재 시간 기준

  const diffMs = end - start;
  if (diffMs < 0) return null;

  const totalMin = Math.round(diffMs / 60000);
  if (totalMin < 1) return '1분 미만';
  if (totalMin < 60) return `${totalMin}분`;
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`;
}

/** 날짜를 YYYY.MM.DD 형식으로 포맷 */
function formatShortDate(date: Date): string {
  return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
}

/**
 * 문서관리 목록용 제목.
 *
 * **저장된 제목을 그대로 쓴다.** 모든 문서가 Composer 문서라 그 제목은 양식이 선언한
 * `titleTemplate`(예: TBM 회의록 `{site} {group} TBM - {date}`)이 생성 시점에 지은 이름이고,
 * 곧 **저작자가 정한 형식**이다. 여기서 다시 조립하면 그 선언이 통째로 버려진다 —
 * `{group}`(공종·분할 그룹)처럼 재조립 규칙에 자리가 없는 축은 **화면에서 영영 사라진다**
 * (2026-09-09 실측: 저장값 「카이토리 사무실 정리정돈·통로관리 TBM - 2026.09.07」이
 * 「카이토리 사무실 3차 TBM 회의록 - (점검예정일)」로 바뀌어 보이고 있었다).
 *
 * 아래 재조립은 **제목이 비어 있을 때의 방어**이고, 유형별 전용 재조립은 두지 않는다.
 *
 * 조건의 축은 **제목 유무** 하나다 — 「templateKey 없는 문서」는 없으므로(`reports.template_key`
 * NOT NULL) 그 항은 아무것도 가르지 못한다. 빈 제목은 이론상 없지만 방어적으로 재조립으로
 * 떨어뜨린다 — 이름 없는 행은 목록에서 고를 수 없기 때문이다.
 */
export function getDocumentDisplayTitle(report: Report): string {
  const siteName = report.site?.name || '';

  if (report.title?.trim()) {
    return report.title;
  }

  const dateSource = report.inspection?.scheduledAt || report.createdAt;
  const dateStr = formatShortDate(new Date(dateSource));
  const seq = report.sequenceNumber ? `${report.sequenceNumber}차 ` : '';

  // 유형 이름은 배지와 같은 헬퍼(양식 이름)에서 온다 — 여기 별도 맵을 두지 않는다(두 벌이면
  // 한쪽만 바뀐다). 코드값 폴백 금지(예전 `|| report.type`이 "과천시 과천읍 COMPOSED - …"를 냈다).
  const typeLabel = getReportTypeLabel(report.templateName);
  return `${siteName} ${seq}${typeLabel} - ${dateStr}`;
}

// ===================================
// PDF 관련 API
// ===================================

export type PdfJobStatus = 'queued' | 'processing' | 'completed' | 'failed' | 'not_started';

export interface PdfGenerationResponse {
  jobId: string;
  status: PdfJobStatus;
}

export interface PdfStatusResponse {
  status: PdfJobStatus;
  progress?: number;
  fileUrl?: string;
  error?: string;
}

/**
 * PDF 생성 요청
 */
export async function generatePdf(
  reportId: string,
  companyId: string
): Promise<PdfGenerationResponse> {
  const response = await api.post<ApiResponse<PdfGenerationResponse>>(
    `/reports/${reportId}/generate-pdf?companyId=${companyId}`
  );
  return response.data.data!;
}

/**
 * PDF 생성 상태 조회
 */
export async function getPdfStatus(
  reportId: string,
  companyId: string
): Promise<PdfStatusResponse> {
  const response = await api.get<ApiResponse<PdfStatusResponse>>(
    `/reports/${reportId}/pdf/status?companyId=${companyId}`
  );
  return response.data.data!;
}

/**
 * PDF 다운로드 (blob으로 받아서 다운로드)
 */
export async function downloadPdf(
  reportId: string,
  companyId: string,
  filename: string
): Promise<void> {
  const response = await api.get(
    `/reports/${reportId}/pdf/download?companyId=${companyId}`,
    { responseType: 'blob' }
  );

  // Blob 다운로드 처리
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * PDF 재생성 요청
 */
export async function regeneratePdf(
  reportId: string,
  companyId: string
): Promise<{ jobId: string; status: string; message: string }> {
  const response = await api.post<ApiResponse<{ jobId: string; status: string; message: string }>>(
    `/reports/${reportId}/pdf/regenerate?companyId=${companyId}`
  );
  return response.data.data!;
}

/**
 * 작성자 서명 취소 (재서명을 위해)
 * DRAFT 상태에서만 가능
 */
export async function cancelAuthorSign(
  reportId: string,
  companyId: string
): Promise<Report> {
  const response = await api.delete<ApiResponse<Report>>(
    `/reports/${reportId}/author-sign?companyId=${companyId}`
  );
  return response.data.data!;
}

// ===================================
// 반려된 보고서 수정 관련 API
// ===================================

export type PhotoOverrideAction = 'REPLACE' | 'DELETE' | 'ADD';

export interface PhotoOverrideDto {
  action: PhotoOverrideAction;
  originalPhotoId?: string; // REPLACE, DELETE 시 필수
  overrideUrl?: string; // REPLACE, ADD 시 필수
  overrideThumbnail?: string;
  displayOrder?: number; // ADD 시 사용
  reason?: string; // 수정 사유
}

export interface ResolutionOverridePhotoItem {
  url: string;
  thumbnailUrl?: string;
  fileId?: string;
}

export interface ResolutionOverrideDto {
  findingId: string;
  notes: string;
  photos?: ResolutionOverridePhotoItem[];
}

export interface PreviousResolutionOverrideDto {
  findingId: string;
  isResolved: boolean;
  notes?: string;
  photos?: ResolutionOverridePhotoItem[];
}

export interface EditReportDto {
  title?: string;
  content?: ReportContent;
  photoOverrides?: PhotoOverrideDto[];
  resolutionOverrides?: ResolutionOverrideDto[];
  previousResolutionOverrides?: PreviousResolutionOverrideDto[];
  editReason?: string; // 전체 수정 사유
}

export interface ResubmitReportDto {
  comment?: string; // 재제출 시 코멘트
}

export interface CanEditResponse {
  canEdit: boolean;
  reason?: string;
  canDeleteAndRecreate: boolean;
  /** 수정 가능 범위: 'all' = 모든 항목, 'own' = 본인 등록 항목만 */
  editableScope?: 'all' | 'own';
  /** 참여자가 수정 가능한 사진 ID 목록 (editableScope='own' 일 때만 유효) */
  editablePhotoIds?: string[];
  /** 참여자가 수정 가능한 발견사항 ID 목록 (editableScope='own' 일 때만 유효) */
  editableFindingIds?: string[];
}

/**
 * 반려된 보고서 수정 가능 여부 확인
 */
export async function canEditReport(
  reportId: string,
  companyId: string
): Promise<CanEditResponse> {
  const response = await api.get<ApiResponse<CanEditResponse>>(
    `/reports/${reportId}/can-edit?companyId=${companyId}`
  );
  return response.data.data!;
}

/**
 * 반려된 보고서 수정
 */
export async function editReport(
  reportId: string,
  companyId: string,
  data: EditReportDto
): Promise<Report> {
  const response = await api.patch<ApiResponse<Report>>(
    `/reports/${reportId}/edit?companyId=${companyId}`,
    data
  );
  return response.data.data!;
}

/**
 * 반려된 보고서 재제출
 */
export async function resubmitReport(
  reportId: string,
  companyId: string,
  data?: ResubmitReportDto
): Promise<Report> {
  const response = await api.post<ApiResponse<Report>>(
    `/reports/${reportId}/resubmit?companyId=${companyId}`,
    data || {}
  );
  return response.data.data!;
}

// (주 담당자 변경·사진 조회/오버라이드 삭제 클라이언트는 없다 — 서버에도 그 엔드포인트가 없다.)

/**
 * 파일 업로드 응답
 */
export interface UploadedFile {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  url: string;
  thumbnailUrl?: string;
  purpose: string;
  createdAt: string;
}

/**
 * 보고서용 파일 업로드
 * 사진 교체 등에 사용
 */
export async function uploadReportFile(
  file: File,
  companyId: string
): Promise<UploadedFile> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('purpose', 'REPORT_ATTACHMENT');
  formData.append('companyId', companyId);

  const response = await api.post<ApiResponse<UploadedFile>>(
    '/files/upload',
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );
  return response.data.data!;
}

// (종합의견·개선대책 요약·정제 조회 클라이언트와 그 sessionStorage 전달은 없다 — Composer 문서의
//  AI 후보는 아래 `generateImprovementCandidates` 하나이고, 정제·종합의견은 서버 resolve가 조립한다.)

export interface ImprovementCandidatesResult {
  guidanceItems: string[];
  summary: string;
  conclusion: string;
  fromCache: { summary: boolean; conclusion: boolean };
  /** guidance 파트에 저장된 사용자 입력 원문(진입 전 "당일 현장 지도사항") — 재진입 기본값용, 없으면 미포함 */
  guidanceInput?: string;
}

export async function generateImprovementCandidates(
  inspectionId: string,
  companyId: string,
  userGuidance?: string,
  parts?: ('guidance' | 'summary' | 'conclusion')[],
): Promise<ImprovementCandidatesResult> {
  const response = await api.post<ApiResponse<ImprovementCandidatesResult>>(
    `/reports/generate-improvement-candidates`,
    { inspectionId, userGuidance, companyId, parts },
  );
  if (!response.data.data) throw new Error('No candidates data');
  return response.data.data;
}

// ============================================
// 보고서 공유 관련 API
// ============================================

/**
 * 관계자 정보 타입
 */
export interface Stakeholder {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

/**
 * 관계자 목록 응답 타입
 */
export interface StakeholdersResponse {
  author: Stakeholder;
  contributors: Stakeholder[];
  approvers: Stakeholder[];
}

/**
 * 공유 수신자 타입
 */
export type ShareRecipient =
  | { type: 'stakeholder'; stakeholderType: 'author' | 'contributor' | 'approver'; id: string }
  | { type: 'direct'; name: string; email?: string; phone?: string };

/**
 * 공유 요청 DTO
 */
export interface ShareReportDto {
  recipients: ShareRecipient[];
  sendVia: ('email' | 'sms')[];
  expiresInDays?: number;
}

/**
 * 공유 결과 응답 타입
 */
export interface ShareReportResponse {
  sharedCount: number;
  shareUrl: string;
  expiresAt: string;
  sentResults: {
    recipientName: string;
    email?: { success: boolean; error?: string };
    sms?: { success: boolean; error?: string };
  }[];
}

/**
 * 보고서 관계자 목록 조회
 */
export async function getReportStakeholders(
  reportId: string,
  companyId: string
): Promise<StakeholdersResponse> {
  const response = await api.get<ApiResponse<StakeholdersResponse>>(
    `/reports/${reportId}/stakeholders?companyId=${companyId}`
  );
  return response.data.data!;
}

/**
 * 보고서 공유
 */
export async function shareReport(
  reportId: string,
  companyId: string,
  dto: ShareReportDto
): Promise<ShareReportResponse> {
  const response = await api.post<ApiResponse<ShareReportResponse>>(
    `/reports/${reportId}/share?companyId=${companyId}`,
    dto
  );
  return response.data.data!;
}

// ============================================
// 보고서 현장 담당자 서명 (Phase 2-H)
// 타입은 @safetyhub/shared 정본 재사용 (파일 상단 re-export)
// ============================================

export async function addReportContactSignature(
  reportId: string,
  companyId: string,
  data: {
    siteContactId?: string;
    signerName: string;
    signerPosition?: string;
    signerPhone?: string;
    signerDepartment?: string;
    signatureData: string;
    signatureMethod: 'DRAWN' | 'TYPED' | 'DIGITAL' | 'INSPECTION';
  },
): Promise<ReportContactSignature> {
  const response = await api.post<ApiResponse<ReportContactSignature>>(
    `/reports/${reportId}/contact-signatures?companyId=${companyId}`,
    data,
  );
  return response.data.data!;
}

export async function deleteReportContactSignature(
  reportId: string,
  sigId: string,
  companyId: string,
): Promise<void> {
  await api.delete(
    `/reports/${reportId}/contact-signatures/${sigId}?companyId=${companyId}`,
  );
}

// ============================================
// Composer 서명 슬롯 (P2 — 조립 행/표 칼럼 서명 수집)
// 서버: reports/report-slot-signatures.controller.ts (인증 — DRAFT·REJECTED만 쓰기)
// 설계: docs/decisions/2026-08-28-row-signature-slot-design.md
// ============================================

/**
 * 슬롯 서명 1건 / GET 응답 맵 — 계약 타입은 shared `SlotSignatureWireView`·`SlotSignatureWireMap`
 * 한 벌이다(서버 `ReportSlotSignaturesService`가 같은 타입을 반환 — 2026-08-30 리뷰 W-reuse6).
 * 종전 로컬 interface는 서버와 따로 놀아 키가 갈려도 tsc가 침묵했다. `signedAt`은 HTTP 직렬화
 * 후 ISO 문자열이다(계약은 `string | Date` — 표시 포맷은 소비처 책임).
 */
export type ReportSlotSignatureView = SlotSignatureWireView;
export type ReportSlotSignatureMap = SlotSignatureWireMap;

export async function getReportSlotSignatures(
  reportId: string,
  companyId: string,
): Promise<ReportSlotSignatureMap> {
  const response = await api.get<ApiResponse<ReportSlotSignatureMap>>(
    `/reports/${reportId}/slot-signatures?companyId=${companyId}`,
  );
  return response.data.data ?? {};
}

/** 슬롯 주소에 서명 저장(같은 주소면 교체 — 재서명 S13). */
export async function putReportSlotSignature(
  reportId: string,
  companyId: string,
  data: {
    slotKey: string;
    rowId?: string;
    signerName: string;
    signerTitle?: string;
    signatureData: string;
  },
): Promise<ReportSlotSignatureView> {
  const response = await api.put<ApiResponse<ReportSlotSignatureView>>(
    `/reports/${reportId}/slot-signatures?companyId=${companyId}`,
    data,
  );
  return response.data.data!;
}

/** 슬롯 주소의 서명 삭제(DRAFT·REJECTED 한정 — 재수집 S13). rowId 생략=단독 슬롯. */
export async function deleteReportSlotSignature(
  reportId: string,
  companyId: string,
  slotKey: string,
  rowId?: string,
): Promise<void> {
  const rowQuery = rowId ? `&rowId=${encodeURIComponent(rowId)}` : '';
  await api.delete(
    `/reports/${reportId}/slot-signatures?companyId=${companyId}&slotKey=${encodeURIComponent(slotKey)}${rowQuery}`,
  );
}

// (단일 발송 클라이언트는 없다 — 모달은 아래 복수 발송 `sendReportContactSignatureLinks`만 쓴다.)

// Phase 3-b: 복수 담당자/채널 링크 발송
export interface SendContactSignatureLinkEntryInput {
  siteContactId?: string;
  signerName: string;
  signerPosition?: string;
  signerDepartment?: string;
  channel: 'SMS' | 'EMAIL' | 'BOTH';
  signerPhone?: string;
  signerEmail?: string;
}

export interface SendContactSignatureLinksResult {
  signatures: Array<{
    id: string;
    signerName: string;
    linkChannel: 'SMS' | 'EMAIL' | 'BOTH';
    tokenExpiresAt: string | null;
    sentAt: string | null;
    failedChannels: Array<'SMS' | 'EMAIL'>;
  }>;
}

/**
 * Phase 3-b: 복수 담당자 × 채널(SMS/EMAIL/BOTH) 서명 링크 발송.
 * 단일 담당자도 entries=[...] 1개로 호출.
 */
export async function sendReportContactSignatureLinks(
  reportId: string,
  companyId: string,
  entries: SendContactSignatureLinkEntryInput[],
): Promise<SendContactSignatureLinksResult> {
  const response = await api.post<ApiResponse<SendContactSignatureLinksResult>>(
    `/reports/${reportId}/contact-signatures/send-links?companyId=${companyId}`,
    { entries },
  );
  return response.data.data!;
}

// getDlrApprovalLines·setDlrApprovalLines 클라이언트는 B1-DLR-6(2026-08-25)에서 제거 —
// 서버의 `:id/dlr-approval-lines` 엔드포인트가 사라졌고, 범용 get/setReportApprovalLines가
// 모든 type을 처리한다. 그 응답 타입 `DlrApprovalLine`(소비처 0)도 2026-09-16 E4에서 함께 걷었다.

// 보고서별 결재라인 관리 (문서 종류 무관)
//
// 응답 행 타입 `ReportApprovalLine`은 shared 계약이다(서버 `getReportApprovalLines`의 손조립 리터럴이
// 같은 타입을 `satisfies`한다 — `ShareReportResponse`와 같은 방식). 종전의 web 로컬 interface는 필드
// 대부분이 optional·`| null`이라 서버가 항상 싣는 키(`signerName`·`unassigned`·`assignmentChain` 등)를
// 화면이 "없을 수도 있는 값"으로 다뤘고, 서버가 키를 빼거나 더해도 아무도 몰랐다. 소비처 import 경로가
// 안 깨지게 여기서 re-export한다.
export type { ReportApprovalLine, ReportApprovalLineMember } from '@safetyhub/shared';

export async function getReportApprovalLines(
  reportId: string,
  companyId: string,
): Promise<ReportApprovalLine[]> {
  const response = await api.get<ApiResponse<ReportApprovalLine[]>>(
    `/reports/${reportId}/approval-lines?companyId=${companyId}`,
  );
  return response.data.data ?? [];
}

/**
 * 전체저장 모델 입력 라인. 편집 모달의 *전체 결재라인*을 순서대로 전송한다.
 * locked 단계도 포함하되 서버가 메타 권위값으로 교정한다(클라 locked 사칭 무시).
 */
export interface SetApprovalLineInput {
  memberId?: string;
  approvalLevel: number;
  label?: string;
  /** 통지 채널 — **정본 축**(2026-08-23 단일화. 구 객체 축 notifyChannels는 서버가 호환 수용만 한다). */
  notifyVia?: ('sms' | 'email')[];
  /**
   * 이 칸의 **지정 방식** — 4종 전체(S76-A). `role`이면 `assignmentRole`도 함께.
   *
   * `role`·`delegatedTo`는 "이 칸은 안 바꿨고 원래 그 지정"이라는 선언이다. 서버는 그 칸의
   * 권위 체인과 일치할 때만 채택하므로 임의 역할을 주입할 수는 없다.
   */
  sourceType?: 'delegatedTo' | 'role' | 'author' | 'member';
  /** `sourceType='role'`일 때의 역할 코드 */
  assignmentRole?: 'SAFETY_MANAGER' | 'OWNER' | 'SITE_CONTACT_BY_SITE';
  /** admin 고정 단계 여부(서버가 권위값으로 재교정) */
  locked?: boolean;
  /**
   * true = **칸은 유지하되 담당자를 지정하지 않는다**(미지정 = 오프라인 수기 결재 칸).
   * 이 마커가 없으면 담당자 없는 행은 서버에서 "빈 행"으로 스킵되어 **칸 자체가 사라진다**.
   * locked 칸에도 적용된다(locked는 칸 구조에 대한 권한이지 담당자에 대한 것이 아님).
   */
  assigneeUnset?: boolean;
}

export async function setReportApprovalLines(
  reportId: string,
  companyId: string,
  lines: SetApprovalLineInput[],
): Promise<ReportApprovalLine[]> {
  // `&type=` 쿼리를 붙이지 말 것 — 서버 whitelist라 보내면 400이다.
  const response = await api.patch<ApiResponse<ReportApprovalLine[]>>(
    `/reports/${reportId}/approval-lines?companyId=${companyId}`,
    // sourceTypesExplicit: 이 번들의 sourceType은 지정 방식을 있는 그대로 말한다(S76-A).
    // 이 표식이 없으면 서버가 구 클라로 보고 "안 바꾼 role/delegatedTo" 추측 복원을 켜서,
    // 사용자가 실제로 바꾼 작성자 지정까지 되돌려 버린다.
    { lines, sourceTypesExplicit: true },
  );
  return response.data.data ?? [];
}

/**
 * 위임 재전송 — `delegatedContactId`를 주면 **담당자 교체**(S25) 또는, 아직 위임하지 않은
 * 보고서라면 **신규 지정**(2026-08-28 결함 ⑧). 엔드포인트는 셋 다 하나다.
 *
 * 서버가 교체·지정을 DRAFT(미제출)에서만 허용한다(담당자가 서명한 뒤 사람을 바꾸면 허위가
 * 된다). 신규 지정에는 양식 게이트가 하나 더 붙는다(`delegationMeta.enabled`).
 */
export async function resendDelegation(
  reportId: string,
  companyId: string,
  channels?: ('sms' | 'email')[],
  message?: string,
  delegatedContactId?: string,
): Promise<{ expiresAt: string; smsStatus: string; emailStatus: string }> {
  const response = await api.post<ApiResponse<{ expiresAt: string; smsStatus: string; emailStatus: string }>>(
    `/reports/${reportId}/resend-delegation?companyId=${companyId}`,
    { channels, message, ...(delegatedContactId ? { delegatedContactId } : {}) },
  );
  return response.data.data!;
}

/**
 * 위임 해제(S25-c) — 담당자 지정만 걷고 작성자가 이어서 쓴다.
 * 취소와 달리 보고서는 **DRAFT로 살아 있고** 새 링크도 나가지 않는다.
 * ⚠️ `null` 본문을 보내면 CORS preflight가 깨진다(ERR_NETWORK) — body는 생략한다.
 */
export async function releaseDelegation(
  reportId: string,
  companyId: string,
): Promise<void> {
  await api.post(
    `/reports/${reportId}/release-delegation?companyId=${companyId}`,
  );
}

export async function cancelDelegation(
  reportId: string,
  companyId: string,
): Promise<void> {
  await api.post(
    `/reports/${reportId}/cancel-delegation?companyId=${companyId}`,
  );
}

// ─── composed TBM 배포 설정(제출 후 흐름) ────────────
//
// 결정 `2026-09-06-tbm-post-submit-distribution-and-delegation.md` §4·§5.
// 서버 정본은 `apps/server/src/reports/tbm-distribution-setup.{controller,service}.ts` +
// `dto/tbm-distribution-setup.dto.ts` — 아래 타입은 그 응답·요청 형상을 그대로 옮긴 것이라,
// 서버 형상이 바뀌면 여기도 같이 바뀌어야 한다(손으로 좁히지 말 것).

/**
 * 배포 설정 진입·실행이 막힌 사유. 판정은 서버 한 곳(`assessTbmDistributionSetupGate`)이
 * 하고 화면은 코드별 안내만 붙인다 — 화면에서 다시 판정하면 두 곳이 갈린다.
 */
export type TbmSetupBlockerCode =
  | 'POST_SUBMIT_NOT_DECLARED'
  | 'APPROVAL_LOCKED'
  | 'ALREADY_CONFIGURED'
  | 'DELEGATION_IN_PROGRESS'
  | 'NOT_ALL_DRAFT';

export interface TbmSetupGate {
  ready: boolean;
  /** 전부 수집된다(첫 사유만 오지 않는다) — 화면이 사유를 다 보여줄 수 있게. */
  blockers: Array<{ code: TbmSetupBlockerCode; reportIds: string[] }>;
}

/** 교육 진행 담당자 후보/추천. `source`는 추천에만 실린다(후보 목록엔 없다). */
export interface TbmSetupEducator {
  contactId: string;
  name: string;
  phone: string | null;
  email: string | null;
  department: string | null;
  position: string | null;
  /** 추천 근거 — 위임받아 작성한 담당자 / 작업유형 매핑. */
  source?: 'delegate' | 'work-mapping';
}

/**
 * 이 그룹 라벨로 **마지막에 확정한 설정**(공종별 학습, 결정 2026-09-07). 화면이 기본값으로
 * 되살린다. 기억이 없는 그룹은 항목 자체가 null이다 — 필드별 null("기억은 있는데 그 값은
 * 없음")과 구별된다.
 */
export interface TbmSetupRememberedSettings {
  signatureMode: TbmSignatureMode | null;
  /** 빈 배열 = 채널 기억 없음(학습 경로가 빈 채널을 쓰는 일이 없다). */
  channels: Array<'sms' | 'email'>;
  reminderTime: string | null;
  /**
   * 기억된 담당자가 **지금은 지정할 수 없다**(비활성·탈퇴·연락 수단 없음). 화면이 조용히
   * 빈칸을 두지 않고 그 자리에 알린다 — 말없이 비어 있으면 사용자는 기억이 원래 없었는지
   * 사라졌는지 알 수 없다.
   */
  educatorUnavailable: boolean;
}

export interface TbmSetupDocument {
  id: string;
  title: string;
  status: ReportStatus;
  splitGroupKey: string | null;
  /** 그룹 표시명. 화면은 이 값을 **인용**만 한다(공종·장소 같은 이름을 붙이지 않는다). */
  splitGroupLabel: string | null;
  /** 건별 저장값(재진입 재표시 재료, W7) — 미설정이면 null. 유효 방식은 건별로 갈릴 수 있다. */
  signatureMode: TbmSignatureMode | null;
  /** OFFLINE 건의 서명란 추가 출력 장수 저장값(기본 0). */
  offlineSheetCount: number;
  delegationState: DelegationProgressState | null;
  recommendedEducator: TbmSetupEducator | null;
  /**
   * 이 문서에 **실제로 확정된** 담당자(2026-09-08 부분 확정 묶음) — 확정 済 형제를 읽기
   * 전용으로 그릴 때 쓴다. 미확정 건은 null(그때는 추천이 기본값이 된다).
   *
   * 추천과 다른 필드인 이유: 추천은 *지금 지정할 수 있는 사람*이고 이건 *그때 확정된 사람*
   * 이다 — 나중에 비활성화·탈퇴해도 확정 사실은 변하지 않으므로 유효 담당자 술어로 거르지
   * 않는다. 도입 전 확정 문서는 담당자가 배포 토큰에만 있어 이름만 실려 올 수 있다.
   */
  confirmedEducator: TbmSetupEducator | null;
  /**
   * 이 문서는 **작성자가 직접 교육을 진행한다**(결정 2026-09-08 「안전관리자가 직접 진행하는
   * TBM 교육」). 담당자 지정의 **배타 갈래**라 이 값이 참이면 담당자·발송 채널이 없다.
   *
   * 부재 추론(`ELECTRONIC + 담당자 null + 토큰 0`)이 아니라 **명시 컬럼**(`Report.
   * tbmSelfConducted`)인 이유는 그 결정의 「판정 축」절 그대로다 — 다른 이유로 토큰이 0이 된
   * 문서(담당자 탈퇴, delegate 배포)가 조용히 직접 진행으로 오인되고, 크론·검증·화면 복원
   * 셋이 각자 추론하면 축이 갈린다.
   */
  selfConducted: boolean;
  /** 공종 기억. 담당자와 달리 **무효 여부와 무관하게** 내려온다(확정 3). */
  rememberedSettings: TbmSetupRememberedSettings | null;
  educationStartDate: string | null;
  educationEndDate: string | null;
  reminderTime: string | null;
}

export interface TbmDistributionSetupData {
  anchorId: string;
  isSplitBundle: boolean;
  /** 묶음 공통 저장값. null이면 미설정(= 설정 완료 전). */
  signatureMode: TbmSignatureMode | null;
  gate: TbmSetupGate;
  documents: TbmSetupDocument[];
  candidates: TbmSetupEducator[];
}

export interface TbmDistributionSetupPayload {
  /** 묶음 공통 기본값 — 건별 예외(documents[].signatureMode)가 없는 문서에 적용된다(W7). */
  signatureMode: TbmSignatureMode;
  /**
   * 실으면 **이번에 설정할 문서**(= 그 묶음의 미확정 건) 정확히 1회씩(모드 무관 — 건별
   * 예외·장수가 여기 실린다). 이미 확정된 형제는 싣지 않는다(서버가 400 — 2026-09-08
   * 부분 확정 묶음). 생략은 공통 OFFLINE 확정 갈래만. 유효 ELECTRONIC 건은 educator 필수
   * (**단 `selfConducted` 건은 예외** — 담당자 없이 통과한다, 결정 2026-09-08 §3),
   * 유효 OFFLINE 건은 offlineSheetCount + **선택적 educator**(연락처 없이 contactId+name만 —
   * 결정 2026-09-08)를 싣고 educationPeriod는 싣지 않는다.
   */
  documents?: Array<{
    reportId: string;
    signatureMode?: TbmSignatureMode;
    offlineSheetCount?: number;
    /**
     * `phone`은 **유효 ELECTRONIC 건에만** 필요하다(서버 검증 축과 같다) — 오프라인 건은
     * 발송이 없어 연락처 없는 사람도 지정할 수 있고, 그 건은 contactId+name만 싣는다.
     */
    educator?: { contactId?: string; name: string; phone?: string; email?: string };
    /**
     * 작성자가 직접 진행한다 — **플래그 하나뿐이고 연락처는 싣지 않는다**(결정 2026-09-08 §2).
     * 화면이 `{name, phone}`을 실어 보낼 수 있게 두면 "본인"이라 주장하며 임의 번호로 문자를
     * 보낼 수 있는 자리가 생긴다. 신원 해소는 서버가 인증된 요청자(`Report.authorId` 일치)로
     * 한다. 그래서 이 갈래에는 `educator`도 `channels`도 없다(보낼 대상이 없다).
     */
    selfConducted?: boolean;
    educationPeriod?: { startDate: string; endDate: string; reminderTime?: string };
    channels?: Array<'sms' | 'email'>;
  }>;
}

export interface TbmDistributionSetupResult {
  /** 진입 문서 — "어느 문서에서 설정했나"다. 묶음의 대표도, 첫 그룹도 아니다. */
  anchorId: string;
  /** 묶음 전 건, **분할 그룹 순서**. 완료 후 이동 대상은 이 배열의 첫 항목(첫 그룹)이다. */
  reportIds: string[];
  signatureMode: TbmSignatureMode;
  distributions: Array<{
    reportId: string;
    tokenId: string;
    contactName: string;
    contactPhone: string;
    smsStatus: string;
    link: string;
  }>;
}

/** 배포 설정 화면 재료 — 묶음 단위(`:id`는 묶음의 아무 문서나 가리킬 수 있다). */
export async function getTbmDistributionSetup(
  reportId: string,
  companyId: string,
): Promise<TbmDistributionSetupData> {
  const response = await api.get<ApiResponse<TbmDistributionSetupData>>(
    `/reports/${reportId}/tbm-distribution-setup?companyId=${companyId}`,
  );
  return response.data.data!;
}

/**
 * 설정+발송 — **한 액션**이다("설정만 저장" 중간 상태가 없다, 구 마법사 동형).
 * 게이트 재판정에 걸리면 409/400과 함께 `details.blocker`에 사유 코드가 실려 온다.
 */
export async function applyTbmDistributionSetup(
  reportId: string,
  companyId: string,
  payload: TbmDistributionSetupPayload,
): Promise<TbmDistributionSetupResult> {
  const response = await api.post<ApiResponse<TbmDistributionSetupResult>>(
    `/reports/${reportId}/tbm-distribution-setup?companyId=${companyId}`,
    payload,
  );
  return response.data.data!;
}
